import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon, EyeIcon, UserGroupIcon, AcademicCapIcon, UsersIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const LessonViewsModal = ({ lessonId, lessonTitle, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (isOpen && lessonId) {
      fetchLessonViews();
    }
  }, [isOpen, lessonId]);

  const fetchLessonViews = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/lessons/${lessonId}/views`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching lesson views:', err);
      alert(err.response?.data?.error || 'Failed to fetch lesson views');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Lesson Views</h2>
            <p className="text-sm text-gray-600 mt-1">{lessonTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : data ? (
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Views</p>
                      <p className="text-3xl font-bold text-blue-900 mt-1">
                        {data.statistics.totalViews}
                      </p>
                    </div>
                    <EyeIcon className="w-10 h-10 text-blue-400" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Unique Users</p>
                      <p className="text-3xl font-bold text-green-900 mt-1">
                        {data.statistics.uniqueUsers}
                      </p>
                    </div>
                    <UserGroupIcon className="w-10 h-10 text-green-400" />
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Student Views</p>
                      <p className="text-3xl font-bold text-purple-900 mt-1">
                        {data.statistics.studentViews}
                      </p>
                    </div>
                    <AcademicCapIcon className="w-10 h-10 text-purple-400" />
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">Teacher Views</p>
                      <p className="text-3xl font-bold text-orange-900 mt-1">
                        {data.statistics.teacherViews}
                      </p>
                    </div>
                    <UsersIcon className="w-10 h-10 text-orange-400" />
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-indigo-600">Total Time</p>
                      <p className="text-3xl font-bold text-indigo-900 mt-1">
                        {formatTime(data.statistics.totalTimeSpent)}
                      </p>
                    </div>
                    <ClockIcon className="w-10 h-10 text-indigo-400" />
                  </div>
                </div>

                <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-pink-600">Avg Time/View</p>
                      <p className="text-3xl font-bold text-pink-900 mt-1">
                        {formatTime(Math.floor(data.statistics.avgTimeSpent))}
                      </p>
                    </div>
                    <ClockIcon className="w-10 h-10 text-pink-400" />
                  </div>
                </div>
              </div>

              {data.statistics.lastViewed && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Last viewed:</span> {formatDate(data.statistics.lastViewed)}
                  </p>
                </div>
              )}

              {/* Toggle Details Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {showDetails ? 'Hide Details' : 'Show Viewer Details'}
                </button>
              </div>

              {/* Detailed View List */}
              {showDetails && data.views.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">View History</h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time Spent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Viewed At
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.views.map((view) => (
                          <tr key={view.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <div className="text-sm font-medium text-gray-900">
                                  {view.user?.name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {view.user?.email || 'N/A'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                view.user?.role === 'student' 
                                  ? 'bg-green-100 text-green-800'
                                  : view.user?.role === 'teacher'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {view.user?.role || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {view.user?.studentType || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-900">
                                <ClockIcon className="w-4 h-4 mr-1 text-indigo-500" />
                                {formatTime(view.timeSpent)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(view.viewedAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {showDetails && data.views.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No views recorded yet
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

LessonViewsModal.propTypes = {
  lessonId: PropTypes.number,
  lessonTitle: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default LessonViewsModal;
